import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { Item as TableItem } from '@baemingo/table-events/dist/types';
import { useApolloClient } from '@apollo/client';
import ArticleItem from '../../../../ComponentsImproved/TSR/ArticleItem';
import DragPreview from '~ComponentsImproved/TSR/DragPreview';
import { DragTypes } from '~ComponentsImproved/TSR/Types';
import { sortByCourseTypeOrder } from '~Utils/sortArticles';
import { useStorage } from '~Components/Storage';
import { combineArticles } from '~Utils/combineArticles';
import { selectItem } from '~Utils/itemSelection';
import { useI18n, useSelector } from '~Hooks';
import OfferItemWrapper from '~Scenes/POS/Components/Cart/Components/OfferItemWrapper';
import { groupOfferMatches } from '~Utils/offers';
import { sortBy } from 'lodash';

const Items = styled.div``;

const OverLay = styled.div<{
  show: boolean;
}>`
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: rgb(0 0 0 / 50%);
  left: 0;
  top: 0;
  z-index: 2;
  pointer-events: none;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
`;

interface Props {
  items: TableItem[];
  staffId: any;
  showLogin?: () => void;
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
  hasPartialPayment?: boolean;
}

const Articles: React.FunctionComponent<Props> = ({
  items,
  staffId,
  showLogin,
  selectedItems,
  setSelectedItems,
  hasPartialPayment,
}) => {
  const client = useApolloClient();
  const { i18n } = useI18n();
  const [itemDrag, setItemDrag] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [storage] = useStorage();
  const venueId = useSelector((state) => state.venueId);

  const dedupedItems = useMemo(() => {
    const seen = new Set();
    return items.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }, [items]);
  const sortedItems = sortByCourseTypeOrder(dedupedItems, client);

  // Group items by offers to show orange grouped headers
  // Uses Tables-specific utility that works directly with TableItem (no conversion needed)
  const grouped = useMemo(() => {
    if (!venueId) {
      return { matches: [], unmatched: sortedItems };
    }
    return groupOfferMatches(sortedItems, venueId, client, i18n);
  }, [sortedItems, venueId, client, i18n]);

  const itemClick = (item: TableItem, isInOfferGroup?: boolean) => {
    if (staffId === null && showLogin) {
      showLogin();
      return;
    }

    setSelectedItems(
      selectItem({
        selectedItem: item,
        items,
        selectedItems,
        compressedCart: isInOfferGroup ? false : storage.compressedCart,
      }),
    );
  };

  useEffect(() => {
    if (isDragging) {
      setItemDrag(true);
    } else if (isDragging == false && itemDrag == true) {
      setSelectedItems([]);
      setItemDrag(false);
    }
  }, [isDragging]);

  /**
   * Renders a single article item with proper selection and drag handling
   */
  const renderArticleItem = (item: TableItem, preview?: boolean, isInOfferGroup?: boolean) => {
    const selected = selectedItems.includes(item.id);
    if (preview && !selected) {
      return null;
    }
    return (
      <ArticleItem
        key={item.id}
        item={item}
        onClick={() => itemClick(item, isInOfferGroup)}
        preview={preview}
        selected={selected}
        selectedItems={selectedItems}
        setIsDragging={setIsDragging}
        hidePrice={isInOfferGroup}
        disallowDrag={hasPartialPayment}
      />
    );
  };

  /**
   * Renders items grouped by offers (with orange headers) and ungrouped items
   */
  const renderItem = (preview?: boolean) => {
    const itemsArr = storage.compressedCart ? combineArticles(sortedItems) : sortedItems;

    return (
      <Items>
        {preview ? (
          itemsArr.map((item) => renderArticleItem(item, preview))
        ) : (
          <>
            {/* Render unmatched items (not part of any offer) */}
            {grouped.unmatched.map((unmatchedItem) => {
              // Find the original TableItem by id, needed because of combineArticles
              const tableItem = itemsArr.find((i) => i.id === unmatchedItem.id);
              if (!tableItem) return null;
              return renderArticleItem(tableItem, preview, false);
            })}

            {/* Render matched offer groups with orange headers */}
            {grouped.matches.map((offerMatch, index) => {
              const offerPrice = offerMatch.items.reduce(
                (acc, item) => {
                  acc.oldPrice += item.amount;
                  acc.newPrice += item.amount - (item.discount ?? 0);

                  return acc;
                },
                {
                  oldPrice: 0,
                  newPrice: 0,
                },
              );

              return (
                <OfferItemWrapper
                  key={`offer-${index}`}
                  offerId={offerMatch.offerId}
                  oldPrice={offerPrice.oldPrice}
                  newPrice={offerPrice.newPrice}>
                  {offerMatch.items.map((matchedItem) => {
                    return renderArticleItem(matchedItem, preview, true);
                  })}
                </OfferItemWrapper>
              );
            })}
          </>
        )}
      </Items>
    );
  };

  return (
    <>
      <OverLay show={isDragging} />
      <DragPreview type={DragTypes.ITEM} renderItem={renderItem(true)} />
      {renderItem()}
    </>
  );
};

export default React.memo(Articles);
